import { useEffect, useMemo } from 'react';
import { getReturnSnList } from '../utils';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { conv2TradeExternalBuyerId } from 'utils/search';
import { Panel } from '../Panel';
import { useTranslate } from '@tolgee/react';
import { Select } from 'antd';

interface PureReturnOrdersProps {
  relatedTradeId: string;
  storeId: string;
  platform: General.PlatformUpperCase;
  externalBuyerId: string;
  readonly?: boolean;
  returnSn?: string;
  autoSelectFirst?: boolean;
  onChange: (returnSn: string) => void;
}

export const PureReturnOrders = (props: PureReturnOrdersProps) => {
  const {
    relatedTradeId,
    storeId,
    platform,
    externalBuyerId,
    readonly = false,
    returnSn,
    autoSelectFirst = false,
    onChange,
  } = props;
  const { t } = useTranslate();

  const { data } = useQuery({
    ...queries.orders.list({
      external_trade_ids: relatedTradeId,
      internal_store_id: storeId,
      external_buyer_id: conv2TradeExternalBuyerId(platform, externalBuyerId),
      page_size: 9999,
      page_no: 1,
    }),
    enabled: !!relatedTradeId && !readonly,
  });

  const trade = useMemo(
    () => data?.tradeList?.find((trade) => trade.externalTradeId === relatedTradeId),
    [data, relatedTradeId],
  );

  const returnSnList = useMemo(() => {
    return getReturnSnList(trade);
  }, [trade]);

  useEffect(() => {
    if (autoSelectFirst && returnSnList.length > 0) {
      onChange(returnSnList[0]);
    }
  }, [autoSelectFirst, returnSnList, onChange]);

  if (!returnSnList || returnSnList.length === 0) {
    return null;
  }

  return (
    <Panel title={t('siderbar.taskTab.title.returnOrder')}>
      <Select
        placeholder={t('sidebar.taskTab.field.returnOrder.placeholder')}
        className="w-full"
        value={returnSn}
        onChange={onChange}
      >
        {returnSnList.map((returnSn) => (
          <Select.Option key={returnSn} value={returnSn}>
            {returnSn}
          </Select.Option>
        ))}
      </Select>
    </Panel>
  );
};
